// src/components/SessionContextPanel.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { trimTrailingSlash } from "../lib/url";
import "./SessionContextPanel.css";

type SessionLite = {
  session_id: string;
  session_date?: string;
  updated_at?: number;
  title?: string;
  notes?: string;
};

type Props = {
  patientId: string;
  apiBaseUrl: string;
  getToken: () => Promise<string>;
  selectedSessionIds: string[];
  onChangeSessions: (ids: string[]) => void;
  selectedAssessmentIds: string[];              // unused here
  onChangeAssessments: (ids: string[]) => void; // unused here
};

const RE_YMD = /^\d{4}-\d{2}-\d{2}$/;
const keyForSessions = (p: string) => `TJ_CONTEXT_SESSIONS_${p || "nopatient"}`;

const shallowEqualSessions = (a: SessionLite[], b: SessionLite[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i], y = b[i];
    if (
      x.session_id !== y.session_id ||
      x.session_date !== y.session_date ||
      x.updated_at !== y.updated_at ||
      x.title !== y.title ||
      x.notes !== y.notes
    ) return false;
  }
  return true;
};

const SessionContextPanel: React.FC<Props> = ({
  patientId,
  apiBaseUrl,
  getToken,
  selectedSessionIds,
  onChangeSessions,
}) => {
  const { ensureToken } = useAuth();

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionLite[]>([]);
  const [filter, setFilter] = useState("");
  const [manualSessionId, setManualSessionId] = useState("");

  const mountedRef = useRef(false);
  const ctrlRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);

  // 🔒 Stabilize possibly-changing functions with refs
  const getTokenRef = useRef(getToken);
  useEffect(() => { getTokenRef.current = getToken; }, [getToken]);

  const ensureTokenRef = useRef(ensureToken);
  useEffect(() => { ensureTokenRef.current = ensureToken; }, [ensureToken]);

  // ✅ Stable, trimmed base URL (string)
  const baseUrl = useMemo(() => trimTrailingSlash(apiBaseUrl || ""), [apiBaseUrl]);

  // ✅ Stable token getter using refs (no dep churn)
  const safeGetToken = useCallback(async (): Promise<string> => {
    const t = (await getTokenRef.current?.()) || (await ensureTokenRef.current?.());
    if (!t) throw new Error("No auth token");
    return t;
  }, []);

  // ✅ Stable loader: depends ONLY on baseUrl + patientId
  const fetchSessions = useCallback(async () => {
    if (!baseUrl || !patientId) {
      if (!mountedRef.current) return;
      setSessions([]);
      return;
    }
    if (inFlightRef.current) return; // prevent overlap / loops
    inFlightRef.current = true;
    setLoading(true);
    setErr(null);

    // abort previous
    try { ctrlRef.current?.abort(); } catch {}
    const ac = new AbortController();
    ctrlRef.current = ac;

    try {
      const token = await safeGetToken();

      const url = new URL(`${baseUrl}/sessions/list`);
      url.searchParams.set("patient_id", patientId);
      url.searchParams.set("limit", "100");

      const resp = await fetch(url.toString(), {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
        signal: ac.signal,
      });

      const text = await resp.text();
      let data: any = {};
      try { data = text ? JSON.parse(text) : {}; } catch {}

      if (!resp.ok || !data?.ok) {
        throw new Error(`List failed ${resp.status} ${resp.statusText} :: ${text?.slice?.(0, 300) || ""}`);
      }

      const raw: any[] = Array.isArray(data.items) ? data.items : [];
      const next: SessionLite[] = raw
        .map((it: any) => ({
          session_id: String(it.session_id || it.id || it.sessionId || ""),
          session_date: it.session_date ? String(it.session_date) : undefined,
          updated_at: typeof it.updated_at === "number" ? it.updated_at : Number(it.updated_at ?? 0) || undefined,
          title: it.title ? String(it.title) : undefined,
          notes: it.notes ? String(it.notes) : undefined,
        }))
        .filter((x: SessionLite) => !!x.session_id);

      next.sort((a, b) => Number(b.updated_at || 0) - Number(a.updated_at || 0));

      if (mountedRef.current) {
        // 🚫 Avoid useless state writes → avoids re-render → avoids loops
        setSessions(prev => (shallowEqualSessions(prev, next) ? prev : next));
      }
    } catch (e: any) {
      if (e?.name !== "AbortError" && mountedRef.current) {
        setErr(e?.message || String(e));
        setSessions([]);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
      inFlightRef.current = false;
      ctrlRef.current = null;
    }
  }, [baseUrl, patientId, safeGetToken]);

  // mount/unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try { ctrlRef.current?.abort(); } catch {}
      inFlightRef.current = false;
    };
  }, []);

  // fetch when baseUrl/patientId change (once per change)
  useEffect(() => { void fetchSessions(); }, [fetchSessions]);

  // external refresh with stable handler
  useEffect(() => {
    const h = () => { void fetchSessions(); };
    window.addEventListener("tj:sessions:refresh", h);
    return () => window.removeEventListener("tj:sessions:refresh", h);
  }, [fetchSessions]);

  // Persist selections per patient
  useEffect(() => {
    try {
      if (patientId) localStorage.setItem(keyForSessions(patientId), JSON.stringify(selectedSessionIds));
    } catch {}
  }, [patientId, selectedSessionIds]);

  // Restore selections on patient change (only if empty)
  useEffect(() => {
    try {
      if (!patientId) return;
      if (selectedSessionIds.length === 0) {
        const raw = localStorage.getItem(keyForSessions(patientId));
        if (raw) onChangeSessions(JSON.parse(raw));
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const filteredSessions = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) =>
      (s.session_id || "").toLowerCase().includes(q) ||
      (s.title || "").toLowerCase().includes(q) ||
      (s.notes || "").toLowerCase().includes(q) ||
      (s.session_date || "").toLowerCase().includes(q)
    );
  }, [sessions, filter]);

  const toggleSession = (id: string) => {
    if (!id) return;
    if (selectedSessionIds.includes(id)) {
      onChangeSessions(selectedSessionIds.filter((x) => x !== id));
    } else {
      onChangeSessions([...selectedSessionIds, id]);
    }
  };

  const addManualSession = () => {
    const id = manualSessionId.trim();
    if (!id) return;
    if (!selectedSessionIds.includes(id)) onChangeSessions([...selectedSessionIds, id]);
    setManualSessionId("");
  };

  return (
    <div className="contextLayout">
      {err && <div className="error" style={{ marginBottom: 8 }}>{err}</div>}

      <section className="card">
        <div className="cardHeader">
          <div className="titleRow">
            <h3>Sessions</h3>
            <div className="searchWrap">
              <input
                className="input"
                placeholder="Filter sessions…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              <span className="kbd">/</span>
            </div>
          </div>
        </div>

        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 28 }}>Sel</th>
                <th style={{ width: "28%" }}>Session ID</th>
                <th style={{ width: "22%" }}>Date</th>
                <th style={{ width: "25%" }}>Title</th>
                <th style={{ width: "25%" }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`skel-s-${i}`} className="skeletonRow"><td colSpan={5} /></tr>
                ))}
              {!loading &&
                filteredSessions.map((s) => (
                  <tr key={s.session_id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedSessionIds.includes(s.session_id)}
                        onChange={() => toggleSession(s.session_id)}
                      />
                    </td>
                    <td className="mono truncate" title={s.session_id}>{s.session_id}</td>
                    <td className="truncate">
                      {RE_YMD.test(String(s.session_date || "")) ? s.session_date : (s.session_date || "-")}
                    </td>
                    <td className="truncate">{s.title || "-"}</td>
                    <td className="truncate">{s.notes || "-"}</td>
                  </tr>
                ))}
              {!loading && filteredSessions.length === 0 && (
                <tr><td colSpan={5} className="empty">No sessions found.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="inlineForm">
          <input
            className="input"
            placeholder="Add session ID manually…"
            value={manualSessionId}
            onChange={(e) => setManualSessionId(e.target.value)}
          />
          <button className="btn" onClick={addManualSession}>Add</button>
        </div>

        {selectedSessionIds.length > 0 && (
          <div className="chipsWrap">
            {selectedSessionIds.map((id) => (
              <button key={id} className="chip" onClick={() => toggleSession(id)} title="Remove">
                {id} ✕
              </button>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default SessionContextPanel;
